import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMMISSION_STATUS_OPTIONS,
  COMMISSION_TYPE_OPTIONS,
  PARTNER_STATUS_OPTIONS,
} from '../../../config/enum-options.js';
import Card from '../../../shared/ui/card/card.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import CommissionRecalculationModal from '../../commission-recalculation/ui/commission-recalculation-modal.jsx';
import { getPartnerStatusBadgeClass } from './partner-status-badge.js';

function getCommissionTypeBadgeClass(type) {
  const normalized = String(type || '').trim().toLowerCase();
  if (normalized === 'cpa') return 'bg-primary-subtle text-primary';
  if (normalized === 'lot_rebate') return 'bg-info-subtle text-info';
  return 'bg-secondary-subtle text-secondary';
}

function getCommissionStatusBadgeClass(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'pending') return 'bg-warning-subtle text-warning';
  if (normalized === 'approved') return 'bg-success-subtle text-success';
  if (normalized === 'paid') return 'bg-info-subtle text-info';
  if (normalized === 'cancelled') return 'bg-secondary-subtle text-secondary';
  return 'bg-secondary-subtle text-secondary';
}

function getDirectReferralStatusBadgeClass(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (['verified', 'active'].includes(normalized)) return 'bg-success-subtle text-success';
  if (['pending', 'verification'].includes(normalized)) return 'bg-warning-subtle text-warning';
  if (normalized === 'new') return 'bg-info-subtle text-info';
  if (normalized === 'completed') return 'bg-primary-subtle text-primary';
  if (normalized === 'disabled') return 'bg-danger-subtle text-danger';
  return 'bg-secondary-subtle text-secondary';
}

function InfoItem({ label, children }) {
  return (
    <div className="d-flex justify-content-between py-1">
      <span className="text-muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function CopyButton({ text }) {
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).catch(() => {});
  };
  return (
    <button type="button" className="btn btn-sm btn-outline-secondary ms-2 py-0 px-1" onClick={handleCopy} title="Copy">
      <i className="ti ti-copy"></i>
    </button>
  );
}

function AssignClientModal({ isOpen, isSaving, profileUid, setProfileUid, error, onSubmit, onClose, t }) {
  if (!isOpen) return null;
  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('partners.assignClient')}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <label className="form-label">{t('partners.profileUid')}</label>
              <input
                type="text"
                className={`form-control${error ? ' is-invalid' : ''}`}
                value={profileUid}
                onChange={(e) => setProfileUid(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                disabled={isSaving}
              />
              {error ? <div className="invalid-feedback">{error}</div> : null}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={onClose} disabled={isSaving}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={isSaving}>{t('common.assign')}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

function PartnerDetailsPageView({
  partner, treeNodes, commissions, isLoading, isSaving, error, success,
  editStatus, setEditStatus, editPlanUid, setEditPlanUid, planOptions,
  showAssignModal, assignProfileUid, setAssignProfileUid, assignError,
  showCommissionRecalculationModal, commissionRecalculationError,
  activeReferralTab, setActiveReferralTab, directReferrals, directReferralsTotal,
  directReferralsQuery, setDirectReferralsQuery, directReferralsOffset, setDirectReferralsOffset,
  directReferralsLimit, isDirectReferralsLoading, loadDirectReferrals,
  commissionTypeFilter, setCommissionTypeFilter, commissionStatusFilter, setCommissionStatusFilter,
  navigateBack, saveStatus, savePlan, openAssignModal, closeAssignModal, submitAssignClient,
  openCommissionRecalculationModal, closeCommissionRecalculationModal, submitCommissionRecalculation,
}) {
  const { t } = useTranslation();
  const [directReferralsDraftQuery, setDirectReferralsDraftQuery] = React.useState(directReferralsQuery);

  React.useEffect(() => {
    setDirectReferralsDraftQuery(directReferralsQuery);
  }, [directReferralsQuery]);

  const directReferralsFrom = directReferralsTotal === 0 ? 0 : directReferralsOffset + 1;
  const directReferralsTo = Math.min(directReferralsOffset + directReferrals.length, directReferralsTotal);
  const hasPreviousDirectReferralsPage = directReferralsOffset > 0;
  const hasNextDirectReferralsPage = directReferralsOffset + directReferrals.length < directReferralsTotal;

  const handleDirectReferralsSearch = (event) => {
    event.preventDefault();
    const nextQuery = directReferralsDraftQuery.trim();
    const nextOffset = 0;
    const shouldReloadImmediately = nextQuery === directReferralsQuery && directReferralsOffset === nextOffset;

    setDirectReferralsOffset(nextOffset);
    setDirectReferralsQuery(nextQuery);

    if (shouldReloadImmediately) {
      loadDirectReferrals({ query: nextQuery, offset: nextOffset });
    }
  };

  const handleDirectReferralsReset = () => {
    const nextQuery = '';
    const nextOffset = 0;
    const shouldReloadImmediately = directReferralsQuery === nextQuery && directReferralsOffset === nextOffset;

    setDirectReferralsDraftQuery(nextQuery);
    setDirectReferralsOffset(nextOffset);
    setDirectReferralsQuery(nextQuery);

    if (shouldReloadImmediately) {
      loadDirectReferrals({ query: nextQuery, offset: nextOffset });
    }
  };

  const handleDirectReferralsPageChange = (nextOffset) => {
    setDirectReferralsOffset(nextOffset);
  };

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('partners.detailsTitle')} subtitle={partner ? partner.profileName : t('partners.partner')} />

      {isLoading ? <div className="text-muted">{t('partners.loadingPartner')}</div> : null}

      {!isLoading && partner ? (
        <>
          <div className="row g-3">
            <div className="col-lg-6">
              <Card title={t('partners.partnerInfo')}>
                <InfoItem label={t('common.status')}>
                  <span className={`badge rounded-pill fw-semibold text-uppercase ${getPartnerStatusBadgeClass(partner.status)}`}>
                    {partner.status}
                  </span>
                </InfoItem>
                <InfoItem label={t('partners.profile')}>
                  <EntityLink to={`/profiles/${encodeURIComponent(partner.profileUid)}`}>{partner.profileName}</EntityLink>
                </InfoItem>
                <InfoItem label={t('partners.referralCode')}>
                  <span>{partner.referralCode}<CopyButton text={partner.referralCode} /></span>
                </InfoItem>
                <InfoItem label={t('partners.referralToken')}>
                  <span className="text-truncate" style={{ maxWidth: '200px', display: 'inline-block', verticalAlign: 'bottom' }}>
                    {partner.referralToken}
                  </span>
                  <CopyButton text={partner.referralToken} />
                </InfoItem>
                <InfoItem label={t('partners.commissionPlan')}>
                  {partner.commissionPlanUid ? (
                    <EntityLink to={`/commission-plans/${encodeURIComponent(partner.commissionPlanUid)}`}>{partner.commissionPlanName}</EntityLink>
                  ) : partner.commissionPlanName}
                </InfoItem>
                <InfoItem label={t('partners.parentPartner')}>
                  {partner.parentUid ? (
                    <EntityLink to={`/partners/${encodeURIComponent(partner.parentUid)}`}>{partner.parentName}</EntityLink>
                  ) : t('partners.none')}
                </InfoItem>
                <InfoItem label={t('common.date')}>{partner.createdAt}</InfoItem>
              </Card>
            </div>

            <div className="col-lg-6">
              <Card title={t('partners.actions')}>
                <div className="mb-3">
                  <label className="form-label">{t('partners.updateStatus')}</label>
                  <div className="d-flex gap-2">
                    <select className="form-select" value={editStatus} onChange={(e) => setEditStatus(e.target.value)} disabled={isSaving}>
                      {PARTNER_STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-primary" onClick={saveStatus} disabled={isSaving}>{t('common.save')}</button>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">{t('partners.changeCommissionPlan')}</label>
                  <div className="d-flex gap-2">
                    <select className="form-select" value={editPlanUid} onChange={(e) => setEditPlanUid(e.target.value)} disabled={isSaving}>
                      <option value="">{t('partners.selectPlan')}</option>
                      {planOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-primary" onClick={savePlan} disabled={isSaving || !editPlanUid}>{t('common.save')}</button>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-outline-primary" onClick={openAssignModal} disabled={isSaving}>
                    <i className="ti ti-user-plus me-1"></i>{t('partners.assignClient')}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={openCommissionRecalculationModal} disabled={isSaving}>
                    <i className="ti ti-refresh me-1"></i>{t('partners.recalculateCommissions')}
                  </button>
                </div>
              </Card>
            </div>
          </div>

          {partner.hasWallet ? (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <Card title={t('partners.wallet')}>
                  <div className="row">
                    <div className="col-md-3"><InfoItem label={t('partners.balance')}>{partner.walletBalanceFormatted}</InfoItem></div>
                    <div className="col-md-3"><InfoItem label={t('partners.totalEarned')}>{partner.walletTotalEarnedFormatted}</InfoItem></div>
                    <div className="col-md-3"><InfoItem label={t('partners.totalWithdrawn')}>{partner.walletTotalWithdrawnFormatted}</InfoItem></div>
                    <div className="col-md-3"><InfoItem label={t('partners.currency')}>{partner.walletCurrency}</InfoItem></div>
                  </div>
                </Card>
              </div>
            </div>
          ) : null}

          <div className="row g-3 mt-1">
            <div className="col-12">
              <Card title={t('partners.referrals')}>
                <ul className="nav nav-tabs mb-3" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link${activeReferralTab === 'tree' ? ' active' : ''}`}
                      onClick={() => setActiveReferralTab('tree')}
                    >
                      {t('partners.referralTree')}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link${activeReferralTab === 'direct-referrals' ? ' active' : ''}`}
                      onClick={() => setActiveReferralTab('direct-referrals')}
                    >
                      {t('partners.directReferrals')}
                    </button>
                  </li>
                </ul>

                {activeReferralTab === 'tree' ? (
                  treeNodes.length === 0 ? (
                    <div className="text-muted">{t('partners.noSubPartners')}</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle text-nowrap mb-0">
                        <thead className="header-item">
                          <tr>
                            <th>{t('common.name')}</th>
                            <th>{t('partners.depth')}</th>
                            <th>{t('partners.referrals')}</th>
                            <th>{t('partners.monthlyEarnings')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {treeNodes.map((node) => (
                            <tr key={node.key}>
                              <td style={{ paddingLeft: `${node.depth * 24 + 12}px` }}>
                                {node.uid ? <EntityLink to={`/partners/${encodeURIComponent(node.uid)}`}>{node.name}</EntityLink> : node.name}
                              </td>
                              <td>{node.depth}</td>
                              <td>{node.referralCount}</td>
                              <td>{node.monthlyEarningsFormatted}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  <>
                    <form className="row g-2 align-items-end mb-3" onSubmit={handleDirectReferralsSearch}>
                      <div className="col-md-5">
                        <label className="form-label">{t('common.search')}</label>
                        <input
                          type="search"
                          className="form-control"
                          value={directReferralsDraftQuery}
                          onChange={(event) => setDirectReferralsDraftQuery(event.target.value)}
                          placeholder={t('partners.searchDirectReferralsPlaceholder')}
                        />
                      </div>
                      <div className="col-md-auto">
                        <button type="submit" className="btn btn-primary" disabled={isDirectReferralsLoading}>
                          {t('common.search')}
                        </button>
                      </div>
                      <div className="col-md-auto">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleDirectReferralsReset} disabled={isDirectReferralsLoading}>
                          {t('common.reset')}
                        </button>
                      </div>
                    </form>

                    <LoadingContainer
                      isLoading={isDirectReferralsLoading}
                      hasData={directReferrals.length > 0}
                      initialText={t('common.loading')}
                      updatingText={t('common.loading')}
                    >
                      <table className="table search-table align-middle text-nowrap mb-0">
                        <thead className="header-item">
                          <tr>
                            <th>{t('common.name')}</th>
                            <th>{t('common.email')}</th>
                            <th>{t('common.status')}</th>
                            <th>{t('common.date')}</th>
                            <th>{t('partners.accounts')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!isDirectReferralsLoading && directReferrals.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">{t('partners.noDirectReferrals')}</td>
                            </tr>
                          ) : null}
                          {directReferrals.map((row) => (
                            <tr key={row.key}>
                              <td>
                                {row.profileUid ? (
                                  <EntityLink to={`/profiles/${encodeURIComponent(row.profileUid)}`}>
                                    {row.fullName}
                                  </EntityLink>
                                ) : row.fullName}
                              </td>
                              <td>{row.email}</td>
                              <td>
                                <span className={`badge rounded-pill fw-semibold text-uppercase ${getDirectReferralStatusBadgeClass(row.status)}`}>
                                  {row.status}
                                </span>
                              </td>
                              <td>{row.createdAt}</td>
                              <td>{row.accountsCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </LoadingContainer>

                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3">
                      <div className="text-muted small">
                        {t('partners.showingReferrals', {
                          from: directReferralsFrom,
                          to: directReferralsTo,
                          total: directReferralsTotal,
                        })}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleDirectReferralsPageChange(Math.max(0, directReferralsOffset - directReferralsLimit))}
                          disabled={!hasPreviousDirectReferralsPage || isDirectReferralsLoading}
                        >
                          {t('partners.previousPage')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleDirectReferralsPageChange(directReferralsOffset + directReferralsLimit)}
                          disabled={!hasNextDirectReferralsPage || isDirectReferralsLoading}
                        >
                          {t('partners.nextPage')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12">
              <Card title={t('partners.commissions')}>
                <div className="d-flex gap-2 mb-3">
                  <select className="form-select form-select-sm" style={{ maxWidth: '160px' }} value={commissionTypeFilter} onChange={(e) => setCommissionTypeFilter(e.target.value)}>
                    {COMMISSION_TYPE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{t(opt.label)}</option>))}
                  </select>
                  <select className="form-select form-select-sm" style={{ maxWidth: '160px' }} value={commissionStatusFilter} onChange={(e) => setCommissionStatusFilter(e.target.value)}>
                    {COMMISSION_STATUS_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{t(opt.label)}</option>))}
                  </select>
                </div>
                {commissions.length === 0 ? (
                  <div className="text-muted">{t('partners.noCommissionsFound')}</div>
                ) : (
                  <table className="table align-middle text-nowrap mb-0">
                    <thead className="header-item">
                      <tr>
                        <th>{t('common.date')}</th>
                        <th>{t('common.type')}</th>
                        <th>{t('partners.client')}</th>
                        <th>{t('partners.depth')}</th>
                        <th>{t('commissions.lots')}</th>
                        <th>{t('commissions.amount')}</th>
                        <th>{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => (
                        <tr key={c.key}>
                          <td>{c.createdAt}</td>
                          <td><span className={`badge rounded-pill fw-semibold text-uppercase ${getCommissionTypeBadgeClass(c.type)}`}>{c.type}</span></td>
                          <td>{c.clientUid ? <EntityLink to={`/profiles/${encodeURIComponent(c.clientUid)}`}>{c.clientName}</EntityLink> : c.clientName}</td>
                          <td>{c.depth}</td>
                          <td>{c.lots}</td>
                          <td>{c.amountFormatted}</td>
                          <td><span className={`badge rounded-pill fw-semibold text-uppercase ${getCommissionStatusBadgeClass(c.status)}`}>{c.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2 mt-4">
            <button type="button" className="btn btn-default" onClick={navigateBack}>{t('partners.backToPartners')}</button>
          </div>
        </>
      ) : null}

      <AssignClientModal
        isOpen={showAssignModal} isSaving={isSaving}
        profileUid={assignProfileUid} setProfileUid={setAssignProfileUid}
        error={assignError} onSubmit={submitAssignClient} onClose={closeAssignModal}
        t={t}
      />
      <CommissionRecalculationModal
        isOpen={showCommissionRecalculationModal}
        isSaving={isSaving}
        title={t('partners.recalculateCommissions')}
        scopeLabel={partner ? t('partners.recalculateCommissionsScope', { name: partner.profileName || partner.profileUid }) : ''}
        error={commissionRecalculationError}
        onSubmit={submitCommissionRecalculation}
        onClose={closeCommissionRecalculationModal}
      />
    </div>
  );
}

export default PartnerDetailsPageView;
